import type { RowSelectionState } from "@tanstack/react-table";

export type SelectionMode = "explicit" | "all-filtered";

export interface TableSelectionState {
  mode: SelectionMode;
  selectedIds: string[];
  excludedIds: string[];
}

export const EMPTY_SELECTION: TableSelectionState = {
  mode: "explicit",
  selectedIds: [],
  excludedIds: [],
};

export function createPageRowSelection(
  state: TableSelectionState,
  pageRowIds: string[],
): RowSelectionState {
  if (state.mode === "all-filtered") {
    const excludedSet = new Set(state.excludedIds);
    return pageRowIds.reduce<RowSelectionState>((acc, id) => {
      if (!excludedSet.has(id)) {
        acc[id] = true;
      }
      return acc;
    }, {});
  }

  const selectedSet = new Set(state.selectedIds);
  return pageRowIds.reduce<RowSelectionState>((acc, id) => {
    if (selectedSet.has(id)) {
      acc[id] = true;
    }
    return acc;
  }, {});
}

export function applyPageSelection(
  state: TableSelectionState,
  pageRowIds: string[],
  nextPageSelection: RowSelectionState,
): TableSelectionState {
  if (state.mode === "all-filtered") {
    const excluded = new Set(state.excludedIds);

    for (const id of pageRowIds) {
      if (nextPageSelection[id]) {
        excluded.delete(id);
      } else {
        excluded.add(id);
      }
    }

    return {
      mode: "all-filtered",
      selectedIds: [],
      excludedIds: [...excluded],
    };
  }

  const selected = new Set(state.selectedIds);

  for (const id of pageRowIds) {
    if (nextPageSelection[id]) {
      selected.add(id);
    } else {
      selected.delete(id);
    }
  }

  return {
    mode: "explicit",
    selectedIds: [...selected],
    excludedIds: [],
  };
}

export function computeSelectedCount(
  state: TableSelectionState,
  filteredRowCount: number | undefined,
): number {
  if (state.mode === "all-filtered") {
    if (typeof filteredRowCount !== "number") {
      return 0;
    }
    return Math.max(0, filteredRowCount - state.excludedIds.length);
  }

  return state.selectedIds.length;
}
